'use client'

import type { Session, User } from '@supabase/supabase-js'

import { getSupabaseClient } from './supabase'
import { withoutTrailingSlash } from './utils'
import { normalizeTenantId } from './tenant'
import { logger } from './logger'
import { type BillingPeriod, type PlanTier, normalizeBillingPeriod } from './billing'
import { extractPlanHintFromLocation, normalizePlanHint } from './plan'
import { resolveDashboardApiUrl } from './dashboard-api'
import type { ProblemDetails } from '@luckagents/shared'

export type AppSession = {
  session: Session
  user: User
  tenantId?: string
}

export async function bootstrapSession(): Promise<AppSession | null> {
  const supabase = getSupabaseClient()
  const { data, error } = await supabase.auth.getSession()

  if (error) {
    logger.error('No fue posible obtener la sesión activa', { error })
    return null
  }

  return mapSession(data.session)
}

export async function login(payload: { email: string; password: string }) {
  const supabase = getSupabaseClient()
  const { data, error } = await supabase.auth.signInWithPassword(payload)

  if (error) {
    throw error
  }

  const mapped = mapSession(data.session)
  if (!mapped) {
    throw new Error('SESSION_NOT_AVAILABLE')
  }

  return mapped
}

export async function register(payload: {
  email: string
  password: string
  name?: string
  organizationName: string
  tenantId?: string
}) {
  const supabase = getSupabaseClient()

  const { data, error } = await supabase.auth.signUp({
    email: payload.email,
    password: payload.password,
    options: {
      emailRedirectTo: buildEmailRedirect(),
      data: {
        name: payload.name,
        organization_name: payload.organizationName,
      },
    },
  })

  if (error) {
    throw error
  }
  const mapped = mapSession(data.session)

  if (mapped) {
    return {
      session: mapped,
      requiresEmailVerification: false,
      user: mapped.user,
    }
  }

  return {
    session: null,
    requiresEmailVerification: true,
    user: data.user,
  }
}

export async function logout() {
  const supabase = getSupabaseClient()
  const { error } = await supabase.auth.signOut()

  if (error) {
    throw error
  }
}

export type DashboardAuthSuccessResponse = {
  status: 'authenticated'
  user: {
    id: string
    email: string
    name?: string
  }
  organization: {
    id: string
    tenantId: string
    name: string
    integrations?: {
      stripe?: StripeIntegrationPayload
    }
  }
  membership: {
    id: string
    role: string
    tenantId: string
    status: string
  }
  auth: {
    token: string
    expiresAt: string
    tenantId: string
    role: string
  }
  refresh: {
    token: string
    sessionId: string
    expiresAt: string
  }
  session: Record<string, unknown>
}

type StripeIntegrationPayload = {
  customerId?: string
  subscriptionId?: string
  checkoutUrl?: string
  portalUrl?: string
  defaultPaymentMethodId?: string
  setupIntentClientSecret?: string
  setupIntentId?: string
}

type DashboardAuthResponse =
  | DashboardAuthSuccessResponse
  | { status: 'mfa_required'; challenge: Record<string, unknown> }
  | Record<string, unknown>

export class DashboardExchangeError extends Error {
  code: string
  status?: number
  retryAfterMs?: number

  constructor(message: string, code?: string, status?: number, retryAfterMs?: number) {
    super(message)
    this.name = 'DashboardExchangeError'
    this.code = code ?? 'dashboard_exchange_failed'
    this.status = status
    this.retryAfterMs = retryAfterMs
  }
}

const isProblemDetails = (payload: unknown): payload is ProblemDetails => {
  if (!payload || typeof payload !== 'object') return false
  const record = payload as Record<string, unknown>
  return (
    typeof record.type === 'string' &&
    typeof record.title === 'string' &&
    typeof record.status === 'number'
  )
}

const normalizeDashboardError = (
  payload: unknown
): { code?: string; message?: string; requestId?: string } => {
  if (!payload || typeof payload !== 'object') return {}
  const record = payload as Record<string, unknown>

  if (isProblemDetails(record)) {
    return {
      code: typeof record.code === 'string' ? record.code : undefined,
      message:
        typeof record.detail === 'string'
          ? record.detail
          : typeof record.title === 'string'
            ? record.title
            : undefined,
      requestId: typeof record.requestId === 'string' ? record.requestId : undefined,
    }
  }

  const code =
    typeof record.error === 'string'
      ? record.error
      : typeof record.error_code === 'string'
        ? record.error_code
        : typeof record.code === 'string'
          ? record.code
          : undefined

  const message =
    typeof record.message === 'string'
      ? record.message
      : typeof record.error_description === 'string'
        ? record.error_description
        : undefined

  const requestId =
    typeof record.requestId === 'string'
      ? record.requestId
      : typeof record.request_id === 'string'
        ? record.request_id
        : typeof record.correlation_id === 'string'
          ? record.correlation_id
          : undefined

  return { code, message, requestId }
}

const DASHBOARD_SSO_ENDPOINT = `${resolveDashboardApiUrl()}/v1/auth/sso/exchange`
const DEVICE_STORAGE_KEY = 'luckagents.deviceId'
const DASHBOARD_EXCHANGE_COOLDOWN_KEY = 'dashboard:sso-exchange-cooldown'
const DASHBOARD_EXCHANGE_DEFAULT_COOLDOWN_MS = 30_000
const DASHBOARD_EXCHANGE_MIN_INTERVAL_MS = 2000

const parseRetryAfterMs = (value?: string | null): number | undefined => {
  if (!value) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const seconds = Number.parseInt(trimmed, 10)
  if (!Number.isNaN(seconds)) return seconds * 1000
  const dateMs = Date.parse(trimmed)
  if (!Number.isNaN(dateMs)) {
    const diff = dateMs - Date.now()
    return diff > 0 ? diff : undefined
  }
  return undefined
}

const getExchangeCooldownUntil = (): number | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(DASHBOARD_EXCHANGE_COOLDOWN_KEY)
    if (!raw) return null
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : null
  } catch {
    return null
  }
}

const setExchangeCooldownUntil = (untilMs: number): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DASHBOARD_EXCHANGE_COOLDOWN_KEY, String(untilMs))
  } catch {
    // ignore storage failures
  }
}

const formatCooldownMessage = (remainingMs: number): string => {
  const seconds = Math.max(1, Math.ceil(remainingMs / 1000))
  return `Demasiados intentos. Espera ${seconds}s y vuelve a intentar.`
}

export const isRateLimitedExchangeError = (error: unknown): error is DashboardExchangeError => {
  return (
    error instanceof DashboardExchangeError &&
    (error.status === 429 || error.code === 'rate_limited')
  )
}

let exchangeInFlight: Promise<DashboardAuthSuccessResponse> | null = null
let exchangeInFlightToken: string | null = null
let lastExchangeAttemptAt = 0
let lastExchangeToken: string | null = null
let lastExchangeResult: DashboardAuthSuccessResponse | null = null
let lastExchangeResultAt = 0
const DASHBOARD_EXCHANGE_SUCCESS_CACHE_MS = 30_000

const extractPlanFromMetadata = (
  metadata: Record<string, unknown> | undefined | null
): PlanTier | null => {
  if (!metadata) return null
  const candidates = [
    metadata.plan,
    metadata.plan_id,
    metadata.planId,
    metadata.billing_plan,
    metadata.billingPlan,
    metadata.subscription_plan,
    metadata.subscriptionPlan,
    metadata.selected_plan,
    metadata.selectedPlan,
    metadata.tier,
    metadata.package,
    metadata.product_plan,
    metadata.productPlan,
  ]

  for (const candidate of candidates) {
    const normalized = normalizePlanHint(candidate)
    if (normalized) return normalized
  }
  return null
}
const resolveDashboardPlan = (session: AppSession, hint?: string | null): PlanTier => {
  const fromHint = normalizePlanHint(hint)
  if (fromHint) return fromHint

  const fromLocation = extractPlanHintFromLocation()
  if (fromLocation) return fromLocation

  const fromUserMetadata = extractPlanFromMetadata(
    session.user.user_metadata as Record<string, unknown>
  )
  if (fromUserMetadata) return fromUserMetadata

  const fromAppMetadata = extractPlanFromMetadata(
    (session.user as unknown as { app_metadata?: Record<string, unknown> }).app_metadata
  )
  if (fromAppMetadata) return fromAppMetadata

  return 'basic'
}

const getDevicePayload = (): { id?: string; name?: string } | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    let deviceId: string | null = null
    try {
      deviceId = window.localStorage.getItem(DEVICE_STORAGE_KEY)
    } catch {
      deviceId = null
    }

    if (!deviceId) {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        deviceId = crypto.randomUUID()
      } else {
        deviceId = Math.random().toString(36).slice(2)
      }
      try {
        window.localStorage.setItem(DEVICE_STORAGE_KEY, deviceId)
      } catch (error) {
        logger.warn('[Auth] No fue posible persistir el identificador de dispositivo', { error })
      }
    }

    const name = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 80) : undefined

    return {
      id: deviceId ?? undefined,
      name,
    }
  } catch (error) {
    logger.warn('[Auth] No fue posible determinar el dispositivo para SSO', { error })
    return undefined
  }
}

export async function exchangeDashboardSession(
  session: AppSession,
  options?: {
    plan?: string | null
    provider?: string | null
    billingPeriod?: BillingPeriod | string | null
    signal?: AbortSignal
  }
): Promise<DashboardAuthSuccessResponse> {
  const accessToken = session.session?.access_token
  if (!accessToken) {
    throw new DashboardExchangeError(
      'No encontramos una sesión activa de Supabase para sincronizar.',
      'missing_supabase_session'
    )
  }

  if (lastExchangeToken === accessToken) {
    const ageMs = Date.now() - lastExchangeResultAt
    if (lastExchangeResult && ageMs > 0 && ageMs < DASHBOARD_EXCHANGE_SUCCESS_CACHE_MS) {
      return lastExchangeResult
    }
  }

  const cooldownUntil = getExchangeCooldownUntil()
  if (cooldownUntil && cooldownUntil > Date.now()) {
    const remainingMs = cooldownUntil - Date.now()
    throw new DashboardExchangeError(
      formatCooldownMessage(remainingMs),
      'rate_limited',
      429,
      remainingMs
    )
  }

  if (exchangeInFlight && exchangeInFlightToken === accessToken) {
    return exchangeInFlight
  }

  const now = Date.now()
  if (now - lastExchangeAttemptAt < DASHBOARD_EXCHANGE_MIN_INTERVAL_MS) {
    const remainingMs = DASHBOARD_EXCHANGE_MIN_INTERVAL_MS - (now - lastExchangeAttemptAt)
    throw new DashboardExchangeError(
      formatCooldownMessage(remainingMs),
      'rate_limited',
      429,
      remainingMs
    )
  }
  lastExchangeAttemptAt = now

  const plan = resolveDashboardPlan(session, options?.plan ?? null)
  const provider = (options?.provider ?? 'supabase').trim() || 'supabase'
  const billingPeriod = normalizeBillingPeriod(options?.billingPeriod ?? null)

  const payload: Record<string, unknown> = {
    provider,
    accessToken,
    plan,
    ...(billingPeriod ? { billingPeriod } : {}),
  }

  const device = getDevicePayload()
  if (device) {
    payload.device = device
  }

  exchangeInFlightToken = accessToken
  exchangeInFlight = (async () => {
    let response: Response
    try {
      response = await fetch(DASHBOARD_SSO_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: options?.signal,
      })
    } catch (error) {
      logger.error('[Auth] No fue posible conectar con el Dashboard API para intercambio SSO', {
        error,
      })
      throw new DashboardExchangeError(
        'No pudimos sincronizar tu sesión con el dashboard. Verifica tu conexión e intenta nuevamente.',
        'network_error'
      )
    }

    let parsed: DashboardAuthResponse | null = null
    try {
      parsed = (await response.json()) as DashboardAuthResponse
    } catch {
      parsed = null
    }

    if (!response.ok) {
      const normalized = normalizeDashboardError(parsed)
      const serverCode = normalized.code
      if (normalized.requestId) {
        logger.warn('[Auth] Dashboard exchange failed', { requestId: normalized.requestId })
      }

      if (response.status === 429) {
        const retryAfterMs =
          parseRetryAfterMs(response.headers.get('Retry-After')) ??
          DASHBOARD_EXCHANGE_DEFAULT_COOLDOWN_MS
        const until = Date.now() + retryAfterMs
        setExchangeCooldownUntil(until)
        throw new DashboardExchangeError(
          formatCooldownMessage(retryAfterMs),
          'rate_limited',
          response.status,
          retryAfterMs
        )
      }

      throw new DashboardExchangeError(
        serverCode === 'supabase_sso_not_configured'
          ? 'El dashboard aún no está listo para recibir intercambios SSO.'
          : serverCode === 'invalid_supabase_token'
            ? 'Tu sesión ya no es válida para el dashboard. Inicia sesión nuevamente desde Acceso.'
            : (normalized.message ??
              'No pudimos sincronizar tu sesión con el dashboard. Intenta nuevamente.'),
        serverCode ?? 'dashboard_exchange_failed',
        response.status
      )
    }

    if (!parsed || typeof parsed !== 'object' || !('status' in parsed)) {
      throw new DashboardExchangeError(
        'Recibimos una respuesta inesperada del dashboard durante el intercambio SSO.',
        'invalid_response_shape'
      )
    }

    if (parsed.status !== 'authenticated') {
      const code = parsed.status === 'mfa_required' ? 'mfa_required' : 'unexpected_status'
      throw new DashboardExchangeError(
        code === 'mfa_required'
          ? 'Necesitas completar un segundo factor en el dashboard antes de continuar.'
          : 'El dashboard respondió con un estado desconocido durante el intercambio SSO.',
        code
      )
    }

    return parsed as DashboardAuthSuccessResponse
  })()

  try {
    const result = await exchangeInFlight
    lastExchangeToken = accessToken
    lastExchangeResult = result
    lastExchangeResultAt = Date.now()
    return result
  } finally {
    exchangeInFlight = null
    exchangeInFlightToken = null
  }
}

function mapSession(session: Session | null): AppSession | null {
  if (!session) return null
  const rawTenantId =
    typeof session.user.user_metadata?.tenant_id === 'string'
      ? session.user.user_metadata.tenant_id
      : null
  const tenantId = normalizeTenantId(rawTenantId) ?? undefined

  return {
    session,
    user: session.user,
    tenantId,
  }
}

function buildEmailRedirect() {
  const portalBase =
    (typeof window !== 'undefined' ? window.location.origin : null) ??
    process.env.NEXT_PUBLIC_PORTAL_URL ??
    process.env.NEXT_PUBLIC_APP_BASE_URL

  const normalized = portalBase ? withoutTrailingSlash(portalBase) : null
  return normalized ? `${normalized}/access` : undefined
}
